import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table } from "reactstrap";
import {
  formatDateWithDayjs,
  sanitizeInput,
  showToast
} from "../../../utils/utils";
import {
  InvoiceDetails,
  SearchText,
  noDataPresent,
  commonText
} from "../../../locales/en";
import PropTypes from "prop-types";
import styles from "./../../../assets/scss/custom.module.scss";
import "./invoicelog.scss";
import { getInvoiceLogData } from "../../../redux/invoiceDetailsSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  COLUMN_COUNT_2,
  showToastType,
  UNKNOWN
} from "../../../utils/constants/appConstants";
import { useVisibleColumnCount } from "../../../hooks/useVisibleColumnCount";
import useScrollToTop from "../../../hooks/useCustomerEventLog";
import EventTableSkeleton from "../../../components/skeletons/EventTableSkeleton";

const Invoicelog = (props) => {
  const dispatch = useDispatch();
  const scrollToTop = useScrollToTop();
  const loading = useSelector(
    (state) => state.invoiceDetails.invLogDataLoading
  );
  const { invData } = props;
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const isTrueRef = useRef(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showDetails, setShowDetails] = useState({});
  const [allLogs, setallLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    if (!invData?.id) return;
    const fetchLogs = async () => {
      try {
        const payload = {
          invoiceNumber: invData?.id,
          keyword: searchQuery,
          showAllRecords: showScroll
        };
        const response = await dispatch(getInvoiceLogData(payload)).unwrap();
        setallLogs(response.data.data);
      } catch (error) {
        setallLogs([]);
        showToast(
          error?.response?.data?.message || showToastType?.errorText,
          showToastType?.error
        );
      }
    };
    fetchLogs();
  }, [searchQuery, invData, showScroll, dispatch]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (!value.trim()) {
      setSearchQuery("");
      setallLogs([]);
      isTrueRef.current = false;
    }
  }, []);

  // Memoized keydown handler
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        const trimmedQuery = tempSearchQuery.trim();
        setSearchQuery(trimmedQuery);
        setallLogs([]);
        isTrueRef.current = false;
      }
    },
    [tempSearchQuery]
  );

  const toggleShow = useCallback((index) => {
    setShowDetails((prevState) => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  }, []);

  const toggleDescription = () => {
    setShowDescription((prev) => {
      const newState = !prev;
      setTimeout(() => {
        const scrollContainer = document.getElementById("hitScroll");
        scrollToTop(scrollContainer);
      }, 0);
      return newState;
    });
    setShowScroll(true);
  };

  return (
    <>
      <div className={styles.titleArea}>
        <h5>{InvoiceDetails?.invoiceLog?.title}</h5>
      </div>
      <div className="row">
        <div className="col-12 mb-4">
          <div className="blog-input-wrap">
            <div className="row d-flex justify-content-end">
              <div className="col-12 col-sm-6 col-md-4">
                <input
                  type="search"
                  value={tempSearchQuery}
                  className={styles.textfield}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  placeholder={SearchText}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 mb-3">
          <div className="event-wrap">
            <div
              className={`event-section ${showDescription ? "event-sec-wrap" : ""}`}
              id="hitScroll"
            >
              <Table
                bordered
                className={`${styles.tableForall} header-bg-primary event-table`}
              >
                <thead ref={headerRef}>
                  <tr>
                    <th>{InvoiceDetails?.invoiceLog?.inTable?.event}</th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <EventTableSkeleton rows={3} columns={visibleColumnCount} />
                  </tbody>
                ) : (
                  <tbody>
                    {allLogs.length === 0 ? (
                      <tr>
                        <td colSpan={COLUMN_COUNT_2} className="text-center">
                          {noDataPresent}
                        </td>
                      </tr>
                    ) : (
                      allLogs.map((event, index) => (
                        <tr key={index}>
                          <td>
                            <div className="content">
                              <div className="mb-2">
                                {event.description && (
                                  <strong
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeInput(event.description)
                                    }}
                                  />
                                )}
                              </div>
                              <div className="content-desc mb-2">
                                <button
                                  className="show-desc-content"
                                  onClick={() => toggleShow(index)}
                                >
                                  {showDetails[index]
                                    ? `${commonText?.hideDetails}`
                                    : `${commonText?.showDetails}`}
                                </button>
                                {showDetails[index] && (
                                  <div className="description-details">
                                    {event.title && (
                                      <p className="mb-2">{event.title}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                              <small>
                                <span className="">
                                  {InvoiceDetails?.invoiceLog?.inTable?.on}
                                </span>
                                <span className="me-1">
                                  {` ${formatDateWithDayjs(event.createdAt)}`}.
                                </span>
                                {event.ipAddress &&
                                event.ipAddress?.toLowerCase() !==
                                  UNKNOWN.toLocaleLowerCase() ? (
                                  <>
                                    {InvoiceDetails?.invoiceLog?.inTable?.ip}
                                    {event.ipAddress}
                                  </>
                                ) : null}
                              </small>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                )}
              </Table>
            </div>
            <button
              className="show-desc-content mt-2"
              onClick={toggleDescription}
            >
              {showDescription
                ? `${commonText?.seeLess}`
                : `${commonText?.seeMore}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

Invoicelog.propTypes = {
  invData: PropTypes.object
};

export default Invoicelog;
